// Construction de l'arborescence de listing à partir du catalog dynamique (RUN-7800).
//
// Le catalog (StoreContext, résolu côté back) pilote :
//   - la hiérarchie des catégories (récursive, N niveaux) — la notion de id_parent GraphQL
//     n'est plus utilisée : une catégorie racine peut devenir enfant dans le catalog ;
//   - l'ordre et le filtrage des items (produits + bannières) ;
//   - les duplications : une même id_category peut apparaître à plusieurs emplacements.
//
// Pour gérer les duplications, chaque nœud reçoit un `path` unique (chemin des id_category
// depuis la racine), utilisé comme clé React et ancre DOM à la place de id_category.

import type {
  IListingCategoryItem,
  IListingCategoryProduct,
} from "./CategoryLayout";
import type { CatalogCategory } from "@src/backbone/model/StoreContextModel";
import type { CategoryItem } from "@src/hooks/query/useAllCategory";
import type CategoryModel from "@src/backbone/model/CategoryModel";

export const CATALOG_PATH_SEPARATOR = ">";

// Suffixe de rang pour les duplications entre frères (RUN-8315) : sans lui, deux occurrences
// d'une même catégorie sous le même parent partagent le même path, donc la même clé React.
// La 1re occurrence garde son path nu (ancres / deep-links existants), les suivantes `~<rang>`.
export const CATALOG_OCCURRENCE_SEPARATOR = "~";

export interface ICatalogTreeResolvers {
  // Données GraphQL d'une catégorie (name, uri, format, banners…)
  getCategoryData: (id_category: string) => CategoryItem | undefined;
  // Modèle Backbone d'une catégorie (état en contexte)
  getCategoryModel: (id_category: string) => CategoryModel | undefined;
  // Produit (données GraphQL + modèle Backbone) résolu par id
  resolveProduct: (id_product: string) => IListingCategoryProduct | undefined;
}

const buildNode = (
  node: CatalogCategory,
  parentPath: string,
  resolvers: ICatalogTreeResolvers,
  // Rang parmi les frères de même id_category (0 = première)
  occurrence = 0
): IListingCategoryItem | null => {
  const data = resolvers.getCategoryData(node.id_category);
  // Catégorie absente du build GraphQL (ex. désactivée) → on l'ignore
  if (!data) return null;

  const basePath = parentPath
    ? `${parentPath}${CATALOG_PATH_SEPARATOR}${node.id_category}`
    : node.id_category;
  const path = occurrence
    ? `${basePath}${CATALOG_OCCURRENCE_SEPARATOR}${occurrence}`
    : basePath;

  // Produits ordonnés selon le catalog (les bannières sont gérées au rendu via catalogItems)
  const productList: IListingCategoryProduct[] = [];
  (node.items || []).forEach((item) => {
    if (item.type === "product") {
      const product = resolvers.resolveProduct(item.id_product);
      if (product) productList.push(product);
    }
  });

  const children = buildSiblings(node.categories, path, resolvers);

  // Élagage : on ignore les branches sans contenu (aucun item propre et aucun enfant
  // conservé) — ex. catégorie conteneur vide dans le contexte courant.
  if (!(node.items?.length || children?.length)) return null;

  return {
    ...data,
    model: resolvers.getCategoryModel(node.id_category),
    path,
    href: data.uri || "",
    productList,
    catalogItems: node.items,
    children,
  };
};

// Fratrie dont les occurrences d'une même id_category sont numérotées (path unique).
// Le compteur est incrémenté avant l'élagage : les paths restent stables si un nœud est retiré.
const buildSiblings = (
  nodes: CatalogCategory[] | undefined,
  parentPath: string,
  resolvers: ICatalogTreeResolvers
): IListingCategoryItem[] | undefined => {
  if (!nodes?.length) return undefined;
  const seen = new Map<string, number>();
  const built = nodes
    .map((node) => {
      const occurrence = seen.get(node.id_category) || 0;
      seen.set(node.id_category, occurrence + 1);
      return buildNode(node, parentPath, resolvers, occurrence);
    })
    .filter((node): node is IListingCategoryItem => node !== null);
  return built.length ? built : undefined;
};

// Construit les nœuds racines de l'arbre de listing à partir du catalog.
export const buildCatalogTree = (
  catalog: CatalogCategory[] | undefined,
  resolvers: ICatalogTreeResolvers
): IListingCategoryItem[] => {
  if (!catalog?.length) return [];
  return buildSiblings(catalog, "", resolvers) || [];
};

// Aplatit l'arbre en liste ordonnée (parcours préfixe / pre-order DFS).
// Chaque nœud conserve son `path` unique : les duplications produisent des entrées distinctes.
export const flattenCatalogTree = (
  nodes: IListingCategoryItem[]
): IListingCategoryItem[] => {
  const flat: IListingCategoryItem[] = [];
  const walk = (list: IListingCategoryItem[]) => {
    list.forEach((node) => {
      flat.push(node);
      if (node.children?.length) walk(node.children);
    });
  };
  walk(nodes);
  return flat;
};
